import { observable, action, computed } from 'mobx';
import { root } from '@estee/elc-universal-utils';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { SearchApiSdk } from '../../api/SearchApiSdk';
import { SearchCatalog } from '../domain/entities/SearchCatalog';
import { SearchFiltersCatalog } from '../domain/entities/SearchFiltersCatalog';
import { SearchPaginationCatalog } from '../domain/entities/SearchPaginationCatalog';
import { SearchTypeaheadCatalog } from '../domain/entities/SearchTypeaheadCatalog';
import { SearchContentCatalog } from '../domain/entities/SearchContentCatalog';
import { SearchArticleCatalog } from '../domain/entities/SearchArticleCatalog';
import {
    IItem,
    ItemTypes,
    IProdObjListItem,
    IContentEnabledConfig
} from '../interfaces/ISearchInterfaces';
import { DISPLAY_SKU_IDS } from '../constants/Search';

const { name, version } = __serviceInfo__;

export interface ISearchQuery {
    searchTerm: string;
    wildCard?: boolean;
    searchDisplayFormat: string;
}

export interface IContentSearchQuery {
    searchTerm: string;
    contentEnabledConfig?: IContentEnabledConfig;
    wildCard?: boolean;
}

export interface ISearchRepository {
    searchApiSdk: SearchApiSdk;
}

export class SearchRepository {
    private searchApiSdk: SearchApiSdk;
    @observable private searchCatalog: SearchCatalog;
    @observable private searchFiltersCatalog: SearchFiltersCatalog;
    @observable private searchPaginationCatalog: SearchPaginationCatalog;
    @observable public searchTypeaheadCatalog: SearchTypeaheadCatalog;
    @observable public searchContentCatalog: SearchContentCatalog;
    @observable public searchArticleCatalog: SearchArticleCatalog;
    @observable public items: IItem[] = [];
    @observable public searchResultsLoaded: boolean = false;
    @observable public searchContentResultsLoaded: boolean = false;
    @observable public searchTypeaheadLoaded: boolean = false;
    private readonly logger: ELCLogger = new ELCLogger({
        serviceName: name,
        environment: root.env,
        buid: root.buid,
        serviceVersion: version
    });

    constructor(props: ISearchRepository) {
        this.searchApiSdk = props.searchApiSdk;
        this.searchCatalog = new SearchCatalog();
        this.searchFiltersCatalog = new SearchFiltersCatalog();
        this.searchPaginationCatalog = new SearchPaginationCatalog();
        this.searchTypeaheadCatalog = new SearchTypeaheadCatalog();
        this.searchContentCatalog = new SearchContentCatalog();
        this.searchArticleCatalog = new SearchArticleCatalog();
    }

    @computed
    public get isTypeaheadProductsLoaded() {
        return this.searchResultsLoaded && this.searchTypeaheadLoaded;
    }

    @action
    public setSearchResultsLoaded = (isLoaded: boolean) => {
        this.searchResultsLoaded = isLoaded;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private updateRedirectUrl = (results: any) => {
        if (results.hasOwnProperty('url')) {
            this.searchCatalog.endecaRedirectUrl = results.url;

            return true;
        }

        return false;
    };

    @action
    public getContentSearchResults = async (contentSearch: IContentSearchQuery) => {
        this.searchContentResultsLoaded = false;

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const searchContentResults: any = await this.searchApiSdk.getContentSearchResults(
                contentSearch
            );
            this.updateRedirectUrl(searchContentResults);
            this.updatefromJsonContent(searchContentResults);

            if (this.content.content.length || this.articles.articles.length) {
                this.searchContentResultsLoaded = true;
            }

            if (
                !contentSearch.wildCard &&
                this.content.content.length === 0 &&
                this.articles.articles.length === 0
            ) {
                contentSearch.wildCard = true;
                const wildCardContentSearchResults = await this.searchApiSdk.getContentSearchResults(
                    contentSearch
                );
                this.updatefromJsonContent(wildCardContentSearchResults);
                this.searchContentResultsLoaded = true;
            }
        } catch (error) {
            this.logger.error({
                message: error.message,
                triggerType: TriggerType.api,
                payload: {
                    error
                }
            });
        }
    };

    @action
    public getSearchResults = async (search: ISearchQuery) => {
        this.searchResultsLoaded = false;

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const searchResults: any = await this.searchApiSdk.getSearchResults(search);

            if (!this.updateRedirectUrl(searchResults)) {
                this.searchCatalog.endecaRedirectUrl = null;
                this.updatefromJsonProducts(searchResults);
                this.searchDisplayResults(search.searchDisplayFormat);

                if (
                    this.searchResults.prodObjListItems.length ||
                    this.searchCatalog.skuIds.length
                ) {
                    this.searchResultsLoaded = true;
                }

                if (
                    !search.wildCard &&
                    (this.searchResults.prodObjListItems.length === 0 ||
                        this.searchCatalog.skuIds.length === 0)
                ) {
                    search.wildCard = true;
                    const wildCardSearchResults = await this.searchApiSdk.getSearchResults(search);
                    this.updatefromJsonProducts(wildCardSearchResults);
                    this.searchDisplayResults(search.searchDisplayFormat);
                    this.searchResultsLoaded = true;
                }
            }
        } catch (error) {
            this.logger.error({
                message: error.message,
                triggerType: TriggerType.api,
                payload: {
                    error
                }
            });
        }
    };

    @action
    public getTypeaheadResults = async (search: ISearchQuery) => {
        this.searchTypeaheadLoaded = false;
        try {
            const searchTypeaheadResults = await this.searchApiSdk.getTypeaheadResults(search);
            this.searchTypeaheadCatalog.update(searchTypeaheadResults);
            this.searchTypeaheadLoaded = true;
        } catch (error) {
            this.logger.error({
                message: error.message,
                triggerType: TriggerType.api,
                payload: {
                    error
                }
            });
        }
    };

    @action
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public updatefromJsonProducts = (searchResults: any) => {
        this.searchCatalog.update(searchResults);
        this.searchFiltersCatalog.update(searchResults);
        this.searchPaginationCatalog.searchPagination(searchResults);
    };

    @action
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public updatefromJsonContent = (searchContentResults: any) => {
        this.searchContentCatalog.update(searchContentResults);
        this.searchArticleCatalog.update(searchContentResults);
    };

    public searchDisplayResults(searchDisplayFormat: string) {
        const productList = this.searchCatalog.prodObjListItems;
        const skuList = this.searchCatalog.skuIds;

        if (searchDisplayFormat === DISPLAY_SKU_IDS) {
            this.items.push(
                ...skuList.map(
                    (skuId: string): IItem => ({
                        type: ItemTypes.sku,
                        params: {
                            id: skuId
                        }
                    })
                )
            );
        } else {
            this.items.push(
                ...productList.map(
                    (itemProp: IProdObjListItem): IItem => ({
                        type: ItemTypes.product,
                        params: {
                            id: itemProp.productId,
                            defaultSkuId: itemProp.defaultSkuId
                        }
                    })
                )
            );
        }

        return this.items;
    }

    @action
    public clearRepositoryItems() {
        this.items = [];
    }

    @computed
    public get searchResults() {
        return this.searchCatalog;
    }

    @computed
    public get filters() {
        return this.searchFiltersCatalog;
    }

    @computed
    public get pagination() {
        return this.searchPaginationCatalog;
    }

    @computed
    public get typeahead() {
        return this.searchTypeaheadCatalog;
    }

    @computed
    public get content() {
        return this.searchContentCatalog;
    }

    @computed
    public get articles() {
        return this.searchArticleCatalog;
    }
}
